import type { Tone } from '../../cc/tokens'
import type { AlertSeverity, AlertShift } from './alertTypes'

/**
 * Human column headings for every alert-analytics column, shared by the on-screen tables
 * (ReportTable `columnDefs.label`) and the Excel export (`exportXlsx`'s `labels`) so the
 * sheet reads exactly like the screen. One map, every view -- a column that appears in
 * two views gets the same word in both.
 */
export const ALERT_LABELS: Record<string, string> = {
  // identity
  alert_id: 'Alert id',
  driver_label: 'Driver',
  driver_id: 'Driver id',
  driver_name: 'Driver',
  vehicle_id: 'Vehicle',
  place: 'Hub / place',
  zone: 'Zone',
  hub_code: 'Hub',
  route_id: 'Route',
  shift: 'Shift',
  kind: 'Alert type',
  severity: 'Severity',
  bucket: 'Bucket',
  dim_value: 'Series',
  // counts
  alerts: 'Alerts',
  critical: 'Critical',
  warn: 'Warnings',
  vehicles: 'Vehicles',
  drivers: 'Drivers',
  kinds: 'Alert types',
  top_kind: 'Top alert type',
  night_alerts: 'Night alerts',
  share_pct: 'Share of shift (%)',
  occurrence_count: 'Occurrences',
  // rates
  km: 'Km driven',
  alerts_per_100km: 'Alerts per 100 km',
  median_speed_kph: 'Median speed (km/h)',
  median_limit_kph: 'Median limit (km/h)',
  // time
  ts: 'Time (IST)',
  last_ts: 'Last seen (IST)',
  first_ts: 'First alert (IST)',
  acknowledged_at: 'Acknowledged at (IST)',
  acknowledged_by: 'Acknowledged by',
  ist_day: 'Day (IST)',
  ist_hour: 'Hour (IST)',
  ist_dow: 'Weekday',
  // position
  lat: 'Latitude',
  lon: 'Longitude',
  message: 'Message',
  // trip
  trip_id: 'Trip',
  round_trip_id: 'Round trip',
  trip_started_ts: 'Trip start (IST)',
  trip_ended_ts: 'Trip end (IST)',
  origin_hub: 'From hub',
  destination_hub: 'To hub',
  load_state: 'Load',
}

export function labelFor(column: string): string {
  return ALERT_LABELS[column] ?? column.replace(/_/g, ' ')
}

/** 'GHAT_OVERSPEED' -> 'Ghat overspeed'. Kinds are upper-snake tokens from the pipeline;
 *  the raw token stays in exports and filters, the readable form is for charts and pills. */
export function kindLabel(kind: string | null | undefined): string {
  if (!kind) return '—'
  if (kind === 'other') return 'Other'
  const words = kind.toLowerCase().split('_').filter(Boolean)
  const first = words[0]
  if (!first) return kind
  return [first.charAt(0).toUpperCase() + first.slice(1), ...words.slice(1)]
    .join(' ')
    .replace(/\bsoc\b/i, 'SoC')
    .replace(/\btat\b/i, 'TAT')
}

export function severityTone(severity: AlertSeverity | string | null | undefined): Tone {
  switch (severity) {
    case 'CRITICAL': return 'danger'
    case 'WARN': return 'warn'
    case 'INFO': return 'info'
    default: return 'neutral'
  }
}

export const SEVERITY_LABEL: Record<AlertSeverity, string> = {
  CRITICAL: 'Critical', WARN: 'Warning', INFO: 'Info',
}

export const SHIFT_LABEL: Record<AlertShift, string> = {
  day: 'Day (06:00–22:00)',
  night: 'Night (22:00–06:00)',
}

export const DOW_LABELS: readonly string[] = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const IST_TS_FMT = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false,
})
const IST_DATE_FMT = new Intl.DateTimeFormat('en-IN', {
  timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short',
})

/** Epoch ms -> "07 Oct, 14:32" in IST; anything non-numeric -> '—'. Every timestamp this
 *  report shows is IST regardless of the viewer's machine, because the fleet is. */
export function fmtIst(ms: unknown): string {
  return typeof ms === 'number' && Number.isFinite(ms) ? IST_TS_FMT.format(new Date(ms)) : '—'
}

/** 'YYYY-MM-DD' day bucket -> "07 Oct". */
export function fmtDayBucket(bucket: string): string {
  const ms = new Date(`${bucket}T00:00:00+05:30`).getTime()
  return Number.isFinite(ms) ? IST_DATE_FMT.format(new Date(ms)) : bucket
}

/** Integer with Indian grouping; null/undefined -> '—'. */
export function fmtInt(v: number | null | undefined): string {
  return typeof v === 'number' && Number.isFinite(v) ? Math.round(v).toLocaleString('en-IN') : '—'
}

/** One decimal, or '—' -- for rates/medians that are null when a site has no km. */
export function fmtDec(v: number | null | undefined, digits = 1): string {
  return typeof v === 'number' && Number.isFinite(v) ? v.toFixed(digits) : '—'
}

/** 0-100 integer percent of part in whole; 0 when whole is 0 (never NaN). */
export function pct(part: number, whole: number): number {
  return whole > 0 ? Math.round((part / whole) * 100) : 0
}

/** '1 vehicle' / '3 vehicles' -- counts in subtitles read as English, not as code. */
export function plural(n: number | null | undefined, one: string, many: string = `${one}s`): string {
  return `${fmtInt(n ?? 0)} ${n === 1 ? one : many}`
}
